import { computed, effect, inject, Injector, Provider, Signal, signal, WritableSignal } from '@angular/core';
import { CxAbstractProvider } from '@b2b/components/cms/cx-abstract/cx-abstract.types';

export abstract class CxAbstractRenderBase<TInput> {
  protected readonly baseInjector = inject(Injector);

  protected abstract readonly renderers: CxAbstractProvider[];
  protected abstract readonly input: Signal<TInput>;

  protected abstract matchRenderer(input: TInput, renderer: CxAbstractProvider): boolean;
  protected abstract createProviders(input: TInput): Provider[];

  protected readonly renderer = computed(() => {
    const inputValue = this.input();
    return this.renderers.find(r => this.matchRenderer(inputValue, r)) ?? null;
  });

  private readonly _componentToRender = computed(() => this.renderer()?.component ?? null);
  get componentToRender() {
    return this._componentToRender();
  }

  protected readonly _componentInjector: WritableSignal<Injector> = signal(this.baseInjector);
  get componentInjector() {
    return this._componentInjector();
  }

  constructor() {
    let once = true;

    effect(() => {
      const renderer = this.renderer();
      if (!renderer) {
        this._componentInjector.set(this.baseInjector);
        return;
      }

      if (once) {
        once = false;

        const input = this.input();

        const injector = Injector.create({
          providers: this.createProviders(input),
          parent: this.baseInjector,
        });

        this._componentInjector.set(injector);
        return;
      }
    });
  }
}
